import React, { FC, useState, useCallback, useEffect, useRef } from 'react'
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Divider,
  Alert,
  Stack,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import axios from 'axios'
import Tree from '../tree/Tree'
import AxisEditor from '../spec-editor/AxisEditor'
import FilterEditorModal from '../spec-editor/FilterEditorModal'
import useVariableTree from '../../hooks/useVariableTree'
import { GenNode } from '../../types/carbon/models/GenNode'
import { API_BASE_URL } from '../../constants/system'
import { processNodes, sortNodesRecursively } from '../../utils/tree'
import { generateNodeId } from '../../utils/axisHelpers'
import { getNodeID } from '../../utils/nodeTypes'

export interface AxisPickerModalProps {
  open: boolean
  onClose: () => void
  onSave: (syntax: string) => void
  customer: string
  job: string
  initialSyntax?: string
  title?: string
}

const AxisPickerModal: FC<AxisPickerModalProps> = ({
  open,
  onClose,
  onSave,
  customer,
  job,
  initialSyntax = '',
  title = 'Top Axis',
}) => {
  const [topNodes, setTopNodes] = useState<GenNode[]>([])
  const [topSyntax, setTopSyntax] = useState('')
  const [axisError, setAxisError] = useState<string | null>(null)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [filterTargetNode, setFilterTargetNode] = useState<GenNode | null>(null)
  const syntaxReqId = useRef(0)

  const variableTree = useVariableTree({ customer, job, loadCodes: true })

  // Convert syntax → axis nodes via Carbon
  const getNodesFromSyntax = useCallback(async (syntax: string): Promise<GenNode[]> => {
    const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/tonodes`
    const res = await axios.post<GenNode[]>(url, { syntax })
    const withIds = processNodes(res.data || [], node => {
      node.id = generateNodeId()
      return node
    })
    const tabAxis = withIds.find(n => n.type === 'TabAxis')
    return sortNodesRecursively(tabAxis?.children || [])
  }, [customer, job])

  const refreshSyntax = useCallback(async (nodes: GenNode[]) => {
    const id = ++syntaxReqId.current
    if (!nodes || nodes.length === 0) {
      setTopSyntax('')
      setAxisError(null)
      return
    }
    if (!customer || !job) return
    try {
      const url = `${API_BASE_URL}/tables/${customer}/${job}/convert/fromnodes`
      const res = await axios.post<string>(url, nodes)
      if (syntaxReqId.current === id) {
        setTopSyntax(res.data || '')
        setAxisError(null)
      }
    } catch (err) {
      console.error('Failed to convert axis nodes to syntax:', err)
      if (syntaxReqId.current === id) {
        setAxisError('Failed to convert axis to syntax')
      }
    }
  }, [customer, job])

  // Reset state and seed from initialSyntax whenever the modal opens
  useEffect(() => {
    if (!open) return
    setAxisError(null)
    variableTree.resetChecked()
    if (initialSyntax && initialSyntax.trim()) {
      getNodesFromSyntax(initialSyntax)
        .then(nodes => {
          setTopNodes(nodes)
          setTopSyntax(initialSyntax)
        })
        .catch(err => {
          console.error('Failed to parse initial syntax:', err)
          setTopNodes([])
          setTopSyntax(initialSyntax)
          setAxisError('Could not parse the existing syntax — start fresh or edit it directly')
        })
    } else {
      setTopNodes([])
      setTopSyntax('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleAxisChange = useCallback((nodes: GenNode[]) => {
    setTopNodes(nodes)
    setAxisError(null)
    refreshSyntax(nodes)
  }, [refreshSyntax])

  const handleSyntaxApply = useCallback(async (syntax: string) => {
    if (!customer || !job) {
      setAxisError('Customer or job is missing')
      throw new Error('Customer or job is missing')
    }
    try {
      const nodes = await getNodesFromSyntax(syntax)
      setTopSyntax(syntax)
      setTopNodes(nodes)
      setAxisError(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to parse syntax'
      setAxisError(message)
      throw err
    }
  }, [customer, job, getNodesFromSyntax])

  // Build canonical Carbon axis syntax fragments from checked variables/codes.
  // Full coverage -> `varname(*)`. Partial coverage -> `varname(c1;c2;...)`.
  // Carbon separators (see carbon/RCS.Carbon.Tables/XTabAxisStringer.cs):
  //   - Codes inside parens: `;` (Defines.DATADELIM)
  //   - Between variables: `,` (handled in handleAddSelected)
  const buildSelectedFragments = useCallback((): string[] => {
    const fragments: string[] = []
    for (const item of variableTree.selectedVariables) {
      const name = (item.value1 || '').trim()
      if (!name) continue
      const allCodes = variableTree.codes[getNodeID(item)] || []
      const checkedCodes = (item.children || [])
        .map(c => (c.value1 || '').trim())
        .filter(Boolean)
      const isFullCoverage = checkedCodes.length === 0 || checkedCodes.length === allCodes.length
      fragments.push(isFullCoverage ? `${name}(*)` : `${name}(${checkedCodes.join(';')})`)
    }
    return fragments
  }, [variableTree.selectedVariables, variableTree.codes])

  const handleAddSelected = useCallback(async () => {
    const fragments = buildSelectedFragments()
    if (fragments.length === 0) return
    const current = topSyntax.trim()
    const addition = fragments.join(',')
    const next = current ? `${current},${addition}` : addition
    try {
      await handleSyntaxApply(next)
      variableTree.resetChecked()
    } catch {
      // error already surfaced via handleSyntaxApply
    }
  }, [buildSelectedFragments, topSyntax, handleSyntaxApply, variableTree])

  // Recursive helper used by Filter save (mirrors useBatchGenerate)
  const updateNodeRecursively = useCallback((arr: GenNode[], targetId: number, updated: GenNode): [GenNode[], boolean] => {
    const idx = arr.findIndex(n => n.id === targetId)
    if (idx !== -1) {
      const copy = [...arr]
      copy[idx] = updated
      return [copy, true]
    }
    let changed = false
    const next = arr.map(n => {
      if (n.children && n.children.length > 0) {
        const [childList, was] = updateNodeRecursively(n.children, targetId, updated)
        if (was) {
          changed = true
          return { ...n, children: childList }
        }
      }
      return n
    })
    return [next, changed]
  }, [])

  const handleSaveFilter = useCallback((filterString: string) => {
    if (!filterTargetNode) return

    if (filterTargetNode.type === 'Filter') {
      const updated: GenNode = { ...filterTargetNode, value1: filterString, value2: filterString }
      const [newTop, changed] = updateNodeRecursively(topNodes, filterTargetNode.id!, updated)
      if (changed) handleAxisChange(newTop)
      setFilterTargetNode(null)
      return
    }

    // Wrap a Codeframe with a Filter
    const wrap = (nodes: GenNode[], targetId: number): [GenNode[], boolean] => {
      const idx = nodes.findIndex(n => n.id === targetId)
      if (idx !== -1) {
        const original = nodes[idx]
        const filterId = generateNodeId()
        const wrappedChild: GenNode = { ...original, parentId: filterId, level: (original.level || 0) + 1 }
        const filterNode: GenNode = {
          type: 'Filter',
          id: filterId,
          parentId: original.parentId,
          isExpanded: true,
          isFolder: true,
          value1: filterString,
          value2: filterString,
          level: original.level || 0,
          anyChildren: true,
          children: [wrappedChild],
        }
        const next = [...nodes]
        next.splice(idx, 1, filterNode)
        return [next, true]
      }
      let changed = false
      const next = nodes.map(n => {
        if (n.children && n.children.length > 0) {
          const [childList, did] = wrap(n.children, targetId)
          if (did) { changed = true; return { ...n, children: childList } }
        }
        return n
      })
      return [next, changed]
    }
    const [newTop, changed] = wrap(topNodes, filterTargetNode.id!)
    if (changed) handleAxisChange(newTop)
    setFilterTargetNode(null)
  }, [filterTargetNode, topNodes, handleAxisChange, updateNodeRecursively])

  const hasSelectedVariables = variableTree.checked.length > 0

  const handleClear = () => {
    setTopNodes([])
    setTopSyntax('')
    setAxisError(null)
    variableTree.resetChecked()
  }

  const handleSave = () => {
    onSave(topSyntax)
    onClose()
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column', borderRadius: 0 } }}
      >
        <DialogContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 0, overflow: 'hidden' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
              {title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="text"
                size="small"
                onClick={handleClear}
                disabled={!topSyntax.trim() && topNodes.length === 0}
                sx={{ color: 'text.secondary' }}
              >
                Clear
              </Button>
              <Button variant="text" size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                Cancel
              </Button>
              <Button variant="contained" size="small" onClick={handleSave} disabled={!topSyntax.trim() || !!axisError}>
                Save
              </Button>
              <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Body: tree + axis editor */}
          <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
            {/* Variable tree */}
            <Box sx={{ width: 360, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.75rem' }}>
                  Variables
                </Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden', p: 1 }}>
                {variableTree.isLoading ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading variables...</Typography>
                ) : variableTree.isError ? (
                  <Alert severity="error" sx={{ m: 1 }}>Failed to load variable tree</Alert>
                ) : variableTree.nodes && variableTree.nodes.length > 0 ? (
                  <Tree
                    searchEnabled
                    showTooltips
                    searchPlaceholder="Search variables..."
                    nodes={variableTree.nodes}
                    expanded={variableTree.expanded}
                    getCheckboxState={variableTree.getCheckboxState}
                    onNodeExpandToggle={variableTree.onNodeExpandToggle}
                    onNodeCheckedToggle={variableTree.onNodeCheckedToggle}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No variables available</Typography>
                )}
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Axis editor */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <AxisEditor
                title={title}
                nodes={topNodes}
                syntax={topSyntax}
                onChange={handleAxisChange}
                onSyntaxApply={handleSyntaxApply}
                onAddSelected={handleAddSelected}
                onOpenFilterModal={(node) => {
                  setFilterTargetNode(node || null)
                  setFilterModalOpen(true)
                }}
                hasSelectedVariables={hasSelectedVariables}
                error={axisError}
              />
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <FilterEditorModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        currentFilter={filterTargetNode?.type === 'Filter' ? (filterTargetNode.value1 || '') : ''}
        onSave={(newFilter) => {
          handleSaveFilter(newFilter)
          setFilterModalOpen(false)
        }}
        customer={customer}
        job={job}
      />
    </>
  )
}

export default AxisPickerModal
